import { Injectable } from '@angular/core';
import { AuthService } from '../auth.service';
import { Observable } from 'rxjs';
import { CompetitionModel, CompetitionFilter, CompetitionParticipantSchoolModel, PublicCompetitionParticipantSchoolFilter } from '../../models/competition/competition.model';
import { 
  CompetitionSchoolActivityModel, 
  CompetitionSchoolActivityGroupedModel,
  CompetitionGroupActivityModel,
  CompetitionSchoolActivityFilter,
  CompetitionGroupActivityFilter
} from '../../models/competition/competition-activity.model';
import { ConsumerCompetitionStats } from '../../models/competition/consumer-competition-stats.model';
import { IntegrationModel } from '../../models/qrcode-integrations/integration.model';
import { map } from 'rxjs/operators';
import { StringExtensionModel } from '../../models/extensions/string-extension.model';

@Injectable({
  providedIn: 'root'
})
export class CompetitionService {

  constructor(private authSv: AuthService) {
  }

  // Public Competition Operations

  fetchPublicCompetitions(filter: CompetitionFilter | undefined): Observable<CompetitionModel[]> {

    let url = '/competitions';

    if (filter) {
      url += StringExtensionModel.queryString(filter);
    }

    return this.authSv.get(url, {}, false).pipe(map((response) => {
      return CompetitionModel.generateModels(response.data);
    }));

  }

  /**
   * The award linked to a competition goal. The endpoint is public so the competition
   * page can show whether the player gets all rewards or picks one.
   */
  fetchPublicIntegrationById(integrationId: string): Observable<IntegrationModel> {

    return this.authSv.get(`/integration-definitions/${integrationId}`, {}, false).pipe(map((response) => {
      return IntegrationModel.generateModel(response.data);
    }));

  }

  fetchPublicCompetitionById(competitionId: string): Observable<CompetitionModel> {

    return this.authSv.get(`/competitions/${competitionId}`, {}, false).pipe(map((response) => {
      return CompetitionModel.generateModel(response.data);
    }));

  }

  /**
   * The running totals of a live competition. Cheap enough to poll. Fails with a
   * CompetitionNotLive error when the competition is not public or not between its start and end.
   */
  fetchPublicLiveCounts(competitionId: string): Observable<{ numCorrect: number, numAnswers: number }> {

    return this.authSv.get(`/competitions/${competitionId}/liveCounts`, {}, false).pipe(map((response) => {
      return {
        numCorrect: response.data.numCorrect || 0,
        numAnswers: response.data.numAnswers || 0
      };
    }));

  }

  /**
   * The stats of a public consumer competition, computed on every request. No per child data.
   */
  fetchPublicConsumerStats(competitionId: string): Observable<ConsumerCompetitionStats> {

    return this.authSv.get(`/competitions/${competitionId}/consumerStats`, {}, false).pipe(map((response) => {
      return ConsumerCompetitionStats.generateModel(response.data);
    }));

  }

  fetchPublicParticipantSchools(competitionId: string, filter: PublicCompetitionParticipantSchoolFilter | undefined): Observable<CompetitionParticipantSchoolModel[]> {

    let url = `/competitions/${competitionId}/participantSchools`;

    if (filter) {
      url += StringExtensionModel.queryString(filter);
    }

    return this.authSv.get(url, {}, false).pipe(map((response) => {
      return CompetitionParticipantSchoolModel.generateModels(response.data);
    }));

  }

  fetchPublicSchoolActivities(
    competitionId: string, 
    filter: CompetitionSchoolActivityFilter | undefined
  ): Observable<CompetitionSchoolActivityModel[]> {

    let url = `/competitions/${competitionId}/schoolActivities`;

    const ungroupedFilter = filter || new CompetitionSchoolActivityFilter();
    ungroupedFilter.group = false;
    if (!filter || filter.type === undefined) {
      ungroupedFilter.type = 'weekly';
    }

    url += StringExtensionModel.queryString(ungroupedFilter);

    return this.authSv.get(url, {}, false).pipe(map((response) => {
      return CompetitionSchoolActivityModel.generateModels(response.data);
    }));

  }

  fetchPublicSchoolActivitiesGrouped(
    competitionId: string, 
    filter: CompetitionSchoolActivityFilter | undefined
  ): Observable<CompetitionSchoolActivityGroupedModel[]> {

    let url = `/competitions/${competitionId}/schoolActivities`;

    const groupedFilter = filter || new CompetitionSchoolActivityFilter();
    groupedFilter.group = true;
    if (!filter || filter.type === undefined) {
      groupedFilter.type = 'weekly';
    }

    url += StringExtensionModel.queryString(groupedFilter);

    return this.authSv.get(url, {}, false).pipe(map((response) => {
      return CompetitionSchoolActivityGroupedModel.generateModels(response.data);
    }));

  }

  // Convenience methods for daily/weekly filtering

  fetchPublicSchoolActivitiesDaily(
    competitionId: string, 
    filter: Omit<CompetitionSchoolActivityFilter, 'type'> | undefined
  ): Observable<CompetitionSchoolActivityModel[]> {
    const dailyFilter = filter as CompetitionSchoolActivityFilter || new CompetitionSchoolActivityFilter();
    dailyFilter.type = 'daily';
    return this.fetchPublicSchoolActivities(competitionId, dailyFilter);
  }

  fetchPublicSchoolActivitiesWeekly(
    competitionId: string, 
    filter: Omit<CompetitionSchoolActivityFilter, 'type'> | undefined
  ): Observable<CompetitionSchoolActivityModel[]> {
    const weeklyFilter = filter as CompetitionSchoolActivityFilter || new CompetitionSchoolActivityFilter();
    weeklyFilter.type = 'weekly';
    return this.fetchPublicSchoolActivities(competitionId, weeklyFilter);
  }

  fetchPublicSchoolActivitiesGroupedDaily(
    competitionId: string, 
    filter: Omit<CompetitionSchoolActivityFilter, 'type'> | undefined
  ): Observable<CompetitionSchoolActivityGroupedModel[]> {
    const dailyFilter = filter as CompetitionSchoolActivityFilter || new CompetitionSchoolActivityFilter();
    dailyFilter.type = 'daily';
    return this.fetchPublicSchoolActivitiesGrouped(competitionId, dailyFilter);
  }

  fetchPublicSchoolActivitiesGroupedWeekly(
    competitionId: string, 
    filter: Omit<CompetitionSchoolActivityFilter, 'type'> | undefined
  ): Observable<CompetitionSchoolActivityGroupedModel[]> {
    const weeklyFilter = filter as CompetitionSchoolActivityFilter || new CompetitionSchoolActivityFilter();
    weeklyFilter.type = 'weekly';
    return this.fetchPublicSchoolActivitiesGrouped(competitionId, weeklyFilter);
  }

  fetchPublicGroupActivities(
    competitionId: string, 
    filter: CompetitionGroupActivityFilter | undefined
  ): Observable<CompetitionGroupActivityModel[]> {

    let url = `/competitions/${competitionId}/groupActivities`;

    const groupFilter = filter || new CompetitionGroupActivityFilter();
    if (!filter || filter.type === undefined) {
      groupFilter.type = 'weekly';
    }

    url += StringExtensionModel.queryString(groupFilter);

    return this.authSv.get(url, {}, false).pipe(map((response) => {
      return CompetitionGroupActivityModel.generateModels(response.data);
    }));

  }

  // Convenience methods for daily/weekly/total filtering for group activities

  fetchPublicGroupActivitiesDaily(
    competitionId: string, 
    filter: Omit<CompetitionGroupActivityFilter, 'type'> | undefined
  ): Observable<CompetitionGroupActivityModel[]> {
    const dailyFilter = filter as CompetitionGroupActivityFilter || new CompetitionGroupActivityFilter();
    dailyFilter.type = 'daily';
    return this.fetchPublicGroupActivities(competitionId, dailyFilter);
  }

  fetchPublicGroupActivitiesWeekly(
    competitionId: string, 
    filter: Omit<CompetitionGroupActivityFilter, 'type'> | undefined
  ): Observable<CompetitionGroupActivityModel[]> {
    const weeklyFilter = filter as CompetitionGroupActivityFilter || new CompetitionGroupActivityFilter();
    weeklyFilter.type = 'weekly';
    return this.fetchPublicGroupActivities(competitionId, weeklyFilter);
  }

  fetchPublicGroupActivitiesTotal(
    competitionId: string, 
    filter: Omit<CompetitionGroupActivityFilter, 'type'> | undefined
  ): Observable<CompetitionGroupActivityModel[]> {
    const totalFilter = filter as CompetitionGroupActivityFilter || new CompetitionGroupActivityFilter();
    totalFilter.type = 'total';
    return this.fetchPublicGroupActivities(competitionId, totalFilter);
  }

}
